import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { UnidadeWip } from '@pieltec/contracts';
import { FormMessage } from '../../components/ui/form-message';
import {
  UNIDADES_WIP,
  ordenarLimites,
  proximaUnidadeLivre,
  type ErroLimiteWip,
  type LinhaLimiteWip,
} from '../../lib/kanban/cadastro-kanban';
import { CampoSelect } from '../../components/ui/campo-select';

interface LimitesWipEditorProps {
  idPrefixo: string;
  linhas: LinhaLimiteWip[];
  onChange: (linhas: LinhaLimiteWip[]) => void;
  erro: ErroLimiteWip | null;
  desabilitado?: boolean;
  /** Texto do estado vazio (padrao: celula). */
  textoSemLimite?: string;
}

const CONTROLE_CLASSE =
  'h-8 px-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white disabled:opacity-60';

/**
 * Editor reutilizavel de linhas unidade + limite de WIP (celula aqui, estufa no 11-20).
 * O limite so avisa no quadro; nunca bloqueia (KAN-05, D-43).
 */
export const LimitesWipEditor: React.FC<LimitesWipEditorProps> = ({
  idPrefixo,
  linhas,
  onChange,
  erro,
  desabilitado = false,
  textoSemLimite = 'Sem limite de WIP: a célula nunca é sinalizada como sobrecarregada.',
}) => {
  const proxima = proximaUnidadeLivre(linhas);

  const atualizarLinha = (indice: number, parcial: Partial<LinhaLimiteWip>) => {
    onChange(linhas.map((linha, i) => (i === indice ? { ...linha, ...parcial } : linha)));
  };

  const remover = (indice: number) => onChange(linhas.filter((_, i) => i !== indice));

  const adicionar = () => {
    if (proxima === null) return;
    onChange(ordenarLimites([...linhas, { unidade: proxima, limite: '' }]));
  };

  return (
    <div className="space-y-2">
      {linhas.length === 0 && (
        <p className="text-[11px] text-slate-500">{textoSemLimite}</p>
      )}

      {linhas.map((linha, indice) => {
        const idLimite = `${idPrefixo}-limite-${indice}`;
        const comErro = erro?.indice === indice;
        return (
          <div key={`${linha.unidade}-${indice}`} className="space-y-1">
            <div className="flex items-center gap-2">
              <CampoSelect
                aria-label="Unidade do limite"
                value={linha.unidade}
                disabled={desabilitado}
                onValueChange={(valor) => atualizarLinha(indice, { unidade: valor as UnidadeWip })}
                options={UNIDADES_WIP.map((unidade) => ({ value: unidade.value, label: unidade.label }))}
                className="w-36"
              />
              <input
                id={idLimite}
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                aria-label="Limite"
                aria-invalid={comErro || undefined}
                aria-describedby={comErro ? `${idLimite}-erro` : undefined}
                value={linha.limite}
                disabled={desabilitado}
                placeholder="Ex.: 8"
                onChange={(e) => atualizarLinha(indice, { limite: e.target.value })}
                className={`${CONTROLE_CLASSE} w-28`}
              />
              {!desabilitado && (
                <button
                  type="button"
                  onClick={() => remover(indice)}
                  className="h-8 px-2 text-xs font-semibold text-slate-600 hover:text-rose-700 hover:bg-slate-100 rounded-md flex items-center gap-1 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remover
                </button>
              )}
            </div>
            {comErro && <FormMessage id={`${idLimite}-erro`}>{erro.mensagem}</FormMessage>}
          </div>
        );
      })}

      {!desabilitado && (
        <button
          type="button"
          onClick={adicionar}
          disabled={proxima === null}
          className="h-8 px-3 text-xs font-semibold text-blue-700 hover:bg-blue-50 rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Adicionar limite
        </button>
      )}
    </div>
  );
};
